import { jwtDecode } from 'jwt-decode';
import { jwtVerify, createRemoteJWKSet } from 'jose';
import type { User } from '@/types/Auth';
import { logger } from '@equip/utils/logger';

// Security Test Coverage Gap (Code Review Issue #CR3)
// CRITICAL: Zero test coverage for security-critical authentication code.
// Missing tests for:
// - JWT signature verification (must reject forged/tampered tokens)
// - PKCE code_challenge generation (RFC 7636 compliance)
// - Authorization code replay detection
// - Token refresh retry with jitter behavior
// - Silent SSO error handling edge cases
// - Idle timeout countdown accuracy
// - Cross-site token validation
// Recommendation: Add integration tests with real crypto operations before production deploy.

interface JwtClaims {
  sub: string;
  preferred_username: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  'https://equip.gov/roles'?: string[];
  'https://equip'?: {
    'gov/roles'?: string[];
  };
  realm_access?: {
    roles?: string[];
  };
  org_id?: string;
  aud: string | string[];
  iss: string;
  exp: number;
  iat: number;
}

// JWKS configuration - lazy initialization to handle URL changes
let jwksCache: ReturnType<typeof createRemoteJWKSet> | null = null;
let cachedJwksUrl: string | null = null;

// Expected values for validation
// Falls back to the current app origin so no port is hardcoded in source.
const getKeycloakUrl = () =>
  import.meta.env.VITE_KEYCLOAK_URL || `${window.location.origin}/realms/equip`;
const EXPECTED_AUDIENCE = ['equip-web-ui', 'account']; // Allow both client ID and account

/**
 * Gets JWKS endpoint with caching and retry logic.
 * Reinitializes if Keycloak URL changes (for testing or multi-tenant scenarios).
 */
const getJWKS = () => {
  const KEYCLOAK_URL = getKeycloakUrl();
  const JWKS_URL = `${KEYCLOAK_URL}/protocol/openid-connect/certs`;

  // Reinitialize if URL changed
  if (!jwksCache || cachedJwksUrl !== JWKS_URL) {
    cachedJwksUrl = JWKS_URL;
    jwksCache = createRemoteJWKSet(new URL(JWKS_URL), {
      cacheMaxAge: 60000, // 60 seconds - faster response to key rotation
      cooldownDuration: 30000, // 30 seconds between retries on failure
    });
  }

  return jwksCache;
};

/**
 * Validates JWT token for security compliance with signature verification.
 * Checks signature, expiration, algorithm, issuer, and audience claims.
 * @throws Error if token is invalid
 */
export const validateToken = async (token: string): Promise<void> => {
  try {
    const KEYCLOAK_URL = getKeycloakUrl();
    const JWKS = getJWKS();

    // Verify signature and validate claims using jose library with retry/caching
    const { payload } = await jwtVerify(token, JWKS, {
      issuer: KEYCLOAK_URL,
      audience: EXPECTED_AUDIENCE,
      algorithms: ['RS256'], // Only allow RS256 (asymmetric signing)
      clockTolerance: 30, // 30-second grace period for clock skew
    });

    // Additional validation for issued-at time
    const now = Date.now() / 1000;
    if (payload.iat && payload.iat > now + 30) {
      throw new Error('Token issued time is in the future');
    }

    logger.debug('Token validation successful', {
      component: 'jwtUtils',
      issuer: payload.iss,
      audience: payload.aud,
    });
  } catch (error) {
    if (error instanceof Error) {
      logger.error('Token validation failed', {
        component: 'jwtUtils',
        error: error.message,
        errorType: error.name,
      });
      throw error;
    }
    throw new Error('Token validation failed: unable to verify token');
  }
};

export const parseJwtClaims = (token: string): User => {
  try {
    const claims = jwtDecode<JwtClaims>(token);

    // Extract roles. realm_access.roles is the authoritative Keycloak claim
    // (populated by IDP mappers for federated users). Custom claim paths are
    // checked as legacy fallbacks.
    // NOTE: use explicit Array.isArray + length checks because `[] || fallback`
    // returns `[]` (arrays are truthy), silently dropping all roles.
    const realmRoles = claims.realm_access?.roles;
    const customRolesFlat = claims['https://equip.gov/roles'];
    const customRolesNested = claims['https://equip']?.['gov/roles'];

    const roles =
      (Array.isArray(realmRoles) && realmRoles.length > 0 ? realmRoles : null) ??
      (Array.isArray(customRolesFlat) && customRolesFlat.length > 0 ? customRolesFlat : null) ??
      (Array.isArray(customRolesNested) && customRolesNested.length > 0 ? customRolesNested : null) ??
      [];

    logger.debug('Resolved JWT roles', {
      component: 'jwtUtils',
      username: claims.preferred_username,
      source: Array.isArray(realmRoles) && realmRoles.length > 0 ? 'realm_access' :
        Array.isArray(customRolesFlat) && customRolesFlat.length > 0 ? 'CIAM flat' :
        Array.isArray(customRolesNested) && customRolesNested.length > 0 ? 'CIAM nested' : 'none',
      roles,
    });

    return {
      id: claims.sub,
      username: claims.preferred_username,
      email: claims.email ?? '',
      firstName: claims.given_name,
      lastName: claims.family_name,
      roles: roles,
      orgId: claims.org_id,
    };
  } catch (error) {
    logger.error('Failed to parse JWT claims', { component: 'jwtUtils', error: String(error) });
    throw new Error('Invalid JWT token');
  }
};

export const getTokenExpiration = (token: string): Date | null => {
  try {
    const claims = jwtDecode<JwtClaims>(token);
    return new Date(claims.exp * 1000);
  } catch {
    return null;
  }
};
